import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/riddle.css'; // Import the CSS file

const Riddle: React.FC = () => {
  const [targetX, setTargetX] = useState(Math.random() * window.innerWidth);
  const [targetY, setTargetY] = useState(Math.random() * window.innerHeight);
  const lightRadiusFactor = 2 / 3; // Configurable factor for the light radius
  const words = ["quantum", "awkward", "zebra", "jazz", "spike", "river", "isolate"];
  const [currentWord, setCurrentWord] = useState(words[Math.floor(Math.random() * words.length)]);
  const [inputText, setInputText] = useState('');
  const [showUnderscore, setShowUnderscore] = useState(true);
  const [isCorrectAnswer, setIsCorrectAnswer] = useState(false); // New state to track if the correct answer is guessed
  const [isIncorrectAnswer, setIsIncorrectAnswer] = useState(false); // New state to track if the answer is incorrect
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null); // Create a ref for the input element

  useEffect(() => {
    const canvas = document.getElementById('spacetime-grid') as HTMLCanvasElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const drawGrid = (x: number, y: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.lineWidth = 1;

      const gridSize = window.innerWidth < 600 ? 15 : 25; // Adjust grid size for mobile screens
      for (let i = 0; i < canvas.width; i += gridSize) {
        for (let j = 0; j < canvas.height; j += gridSize) {
          const dx = i - x;
          const dy = j - y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const warpFactor = Math.exp(-distance / 200);

          ctx.beginPath();
          ctx.moveTo(i, j);
          ctx.lineTo(i - gridSize * warpFactor, j);
          ctx.lineTo(i - gridSize * warpFactor, j - gridSize * warpFactor);
          ctx.lineTo(i, j - gridSize * warpFactor);
          ctx.closePath();
          ctx.stroke();
        }
      }
    };

    const drawLight = (x: number, y: number) => {
      const oppositeX = canvas.width - x;
      const oppositeY = canvas.height - y;

      const dx = targetX - oppositeX;
      const dy = targetY - oppositeY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const greenFactor = Math.max(0, 1 - distance / 500);

      const lightRadius = 200 * lightRadiusFactor; // Adjust the light radius based on the factor

      const gradient = ctx.createRadialGradient(oppositeX, oppositeY, 0, oppositeX, oppositeY, lightRadius);
      gradient.addColorStop(0, `rgba(${255 * (1 - greenFactor)}, 255, ${255 * (1 - greenFactor)}, 0.5)`);
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw the word at the target coordinates
      const wordVisibility = Math.min(0.1 + greenFactor * 0.9, 0.5); // Limit the visibility to 0.5
      ctx.font = "10px monospace";
      ctx.fillStyle = `rgba(0, 0, 0, ${1 - wordVisibility})`; // Make the word less dark
      ctx.fillText(currentWord, targetX, targetY);
    };

    const handleMouseMove = (e: MouseEvent) => {
      drawGrid(e.clientX, e.clientY);
      drawLight(e.clientX, e.clientY);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        drawGrid(e.touches[0].clientX, e.touches[0].clientY);
        drawLight(e.touches[0].clientX, e.touches[0].clientY);
      }
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        inputRef.current?.focus(); // Focus the input element on tap
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchstart', handleTouchStart);

    const interval = setInterval(() => {
      setTargetX(Math.random() * window.innerWidth);
      setTargetY(Math.random() * window.innerHeight);
      setCurrentWord(words[Math.floor(Math.random() * words.length)]);
    }, 5000);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('resize', resizeCanvas);
      clearInterval(interval);
    };
  }, [targetX, targetY, currentWord]);

  useEffect(() => {
    const underscoreInterval = setInterval(() => {
      setShowUnderscore(prev => !prev);
    }, 500);

    return () => clearInterval(underscoreInterval);
  }, []);

  useEffect(() => {
    inputRef.current?.focus(); // Focus the input element when the component mounts
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= 10) {
      setInputText(value);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (inputText.toLowerCase() === 'quantum') {
        setIsCorrectAnswer(true); // Set the correct answer state to true
        setTimeout(() => {
          navigate('/truth'); // Redirect to the new page
        }, 2000); // Adjust the delay as needed for the animation
      } else {
        setIsIncorrectAnswer(true); // Set the incorrect answer state to true
        setTimeout(() => {
          setIsIncorrectAnswer(false); // Reset the incorrect answer state
          setInputText(''); // Clear the input text
          inputRef.current?.focus(); // Focus the input element
        }, 2000); // Adjust the delay as needed
      }
    }
  };

  return (
    <div
      style={{
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
        backgroundColor: "black",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        flexDirection: "column",
        color: "white",
        fontFamily: "monospace",
        padding: "20px", // Add padding to the main container
        boxSizing: "border-box", // Ensure padding is included in the element's total width and height
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "10px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 2,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          padding: "10px",
          borderRadius: "5px",
          textAlign: "center",
          width: "90%", // Ensure the text container is responsive
          maxWidth: "600px", // Limit the maximum width
        }}
      >
        <p>In the realm where light bends and shadows play,</p>
        <p>A hidden truth awaits, not far away.</p>
        <p>With every step, the grid aligns,</p>
        <p>A dance of fate, in space and time.</p>
        <p>Seek the word that shifts and gleams,</p>
        <p>In hues of green, within your dreams.</p>
        <p>A quantum leap, a chance to find,</p>
        <p>The answer lies within your mind.</p>
        <p>Through light's embrace, the path is clear,</p>
        <p>A fleeting glimpse, the end is near.</p>
        <p>With luck and speed, the truth you'll see,</p>
        <p>The quantum key to set you free.</p>
      </div>
      <canvas
        id="spacetime-grid"
        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
      ></canvas>
      <div
        style={{
          position: "relative",
          zIndex: 1,
          textAlign: "center",
        }}
      >
        <input
          type="text"
          value={inputText}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          ref={inputRef} // Attach the ref to the input element
          style={{
            background: "none",
            border: "none",
            borderBottom: "2px solid white",
            color: "white",
            fontSize: "2rem",
            textAlign: "center",
            outline: "none",
            width: "10ch",
            position: "absolute",
            opacity: 0,
          }}
        />
        <div style={{ fontSize: "2rem", marginTop: "20px" }}>
          {isIncorrectAnswer ? (
            <span style={{ color: "red" }}>❌</span>
          ) : (
            inputText.split('').map((char, index) => (
              <span key={index} className={isCorrectAnswer ? 'glitch' : ''}>{char}</span>
            ))
          )}
          {!isCorrectAnswer && !isIncorrectAnswer && showUnderscore && <span style={{ color: "white" }}>_</span>}
        </div>
      </div>
      <style>
        {`
          @media (max-width: 600px) {
            div {
              padding-top: 80px; /* Increase padding for mobile devices */
            }
          }
        `}
      </style>
    </div>
  );
};

export default Riddle;